import { useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { describe } from '../errors';
import type { ExportOption, ImportReport } from '../api-modules';
import { useI18n } from '../i18n';
import { EmptyState, Field, Skeleton, Spinner } from '../components';

/**
 * Getting data in and out (spec §16).
 *
 * Deliberately one screen for both directions. They are the same question from
 * a clinic's point of view — "is my data mine?" — and keeping the import
 * buried inside the patient list made it feel like a hidden setting rather
 * than a stated property of the system.
 */
export function Transfer({ canImport }: { canImport: boolean }) {
  return (
    <div className="stack">
      <ExportPanel />
      {canImport ? <ImportPanel /> : null}
    </div>
  );
}

/* ------------------------------------------------------------------ export */

function ExportPanel() {
  const { t } = useI18n();
  const [options, setOptions] = useState<ExportOption[] | null>(null);
  const [dentists, setDentists] = useState<{ membershipId: string; name: string }[]>([]);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [dentistId, setDentistId] = useState('');
  const [busyKind, setBusyKind] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .exportCatalogue()
      .then((r) => setOptions(r.exports))
      .catch((e) => setError(describe(e, t)));
    api
      .dentists()
      .then((r) => setDentists(r.dentists.map((d) => ({ membershipId: d.membershipId, name: d.name }))))
      // A caller without dentist.manage still gets every other export; an
      // empty picker is the right outcome, not an error banner.
      .catch(() => setDentists([]));
  }, [t]);

  async function run(kind: string) {
    setBusyKind(kind);
    setError(null);
    setDone(null);
    try {
      const result = await api.downloadExport(kind, {
        from: from || undefined,
        to: to || undefined,
        dentistId: dentistId || undefined,
      });
      setDone(
        t.transfer.downloaded
          .replace('{name}', result.filename)
          .replace('{rows}', String(result.rows)),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : describe(e, t));
    } finally {
      setBusyKind(null);
    }
  }

  return (
    <div className="card">
      <div className="card-head">
        <h2>{t.transfer.exportTitle}</h2>
      </div>
      <div className="card-body stack">
        <p style={{ color: 'var(--ink-3)', margin: 0, fontSize: 13 }}>{t.transfer.exportHint}</p>

        {error ? (
          <div className="notice danger" role="alert">
            {error}
          </div>
        ) : null}
        {done ? (
          <div className="notice ok">
            <span>{done}</span>
          </div>
        ) : null}

        {/*
          One set of filters above the list rather than per row. Most exports
          ignore them, and each row says whether it uses them — which is less
          noisy than repeating three date pickers eight times.
        */}
        <div className="grid-3">
          <Field label={t.transfer.from} hint={t.transfer.wholeHistory}>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="ltr-value" />
          </Field>
          <Field label={t.transfer.to}>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="ltr-value" />
          </Field>
          <Field label={t.transfer.chooseDentist}>
            <select value={dentistId} onChange={(e) => setDentistId(e.target.value)}>
              <option value="">—</option>
              {dentists.map((d) => (
                <option key={d.membershipId} value={d.membershipId}>
                  {d.name}
                </option>
              ))}
            </select>
          </Field>
        </div>

        {options === null ? (
          <Skeleton rows={4} />
        ) : (
          <div className="table-scroll">
            <table>
              <tbody>
                {options.map((option) => {
                  const needsDentist = option.takesDentist && option.kind === 'patients-by-dentist';
                  const blocked = !option.available || (needsDentist && !dentistId);
                  return (
                    <tr key={option.kind}>
                      <td>
                        <strong>
                          {(t.transfer.exportKinds as Record<string, string>)[option.kind] ?? option.kind}
                        </strong>
                        <span className="sub" style={{ display: 'block', color: 'var(--ink-3)', fontSize: 12 }}>
                          {!option.available
                            ? t.transfer.needsPermission
                            : [
                                option.takesRange ? `${t.transfer.from}/${t.transfer.to}` : null,
                                option.takesDentist ? t.transfer.chooseDentist : null,
                              ]
                                .filter(Boolean)
                                .join(' · ') || '—'}
                        </span>
                      </td>
                      <td style={{ textAlign: 'end', whiteSpace: 'nowrap' }}>
                        <button
                          type="button"
                          className="btn btn-sm"
                          disabled={blocked || busyKind !== null}
                          onClick={() => void run(option.kind)}
                        >
                          {busyKind === option.kind ? (
                            <>
                              <Spinner /> {t.transfer.downloading}
                            </>
                          ) : (
                            t.transfer.download
                          )}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ import */

function ImportPanel() {
  const { t } = useI18n();
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<{ name: string; content: string } | null>(null);
  const [report, setReport] = useState<ImportReport | null>(null);
  const [withDuplicates, setWithDuplicates] = useState(false);
  const [busy, setBusy] = useState<'checking' | 'importing' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  async function choose(chosen: File) {
    setError(null);
    setDone(null);
    setReport(null);
    const buffer = await chosen.arrayBuffer();
    // btoa cannot take a whole binary string at once for large files; chunked
    // conversion keeps a few thousand patients from blowing the call stack.
    let binary = '';
    const bytes = new Uint8Array(buffer);
    for (let i = 0; i < bytes.length; i += 8192) {
      binary += String.fromCharCode(...bytes.subarray(i, i + 8192));
    }
    const content = btoa(binary);
    setFile({ name: chosen.name, content });

    setBusy('checking');
    try {
      setReport(await api.previewImport(content, chosen.name));
    } catch (e) {
      setError(describe(e, t));
    } finally {
      setBusy(null);
    }
  }

  async function commit() {
    if (!file || !report) return;
    setBusy('importing');
    setError(null);
    try {
      const lines = withDuplicates
        ? report.rows.filter((r) => r.status === 'duplicate').map((r) => r.line)
        : [];
      const result = await api.commitImport(file.content, file.name, lines);
      setDone(t.transfer.imported.replace('{n}', String(result.imported)));
      setReport(null);
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
    } catch (e) {
      setError(describe(e, t));
    } finally {
      setBusy(null);
    }
  }

  const importable = report
    ? report.ready + (withDuplicates ? report.duplicates : 0)
    : 0;

  return (
    <div className="card">
      <div className="card-head">
        <h2>{t.transfer.importTitle}</h2>
        <button
          type="button"
          className="btn-link"
          onClick={() => void api.downloadImportTemplate().catch((e) => setError(describe(e, t)))}
        >
          {t.transfer.getTemplate}
        </button>
      </div>
      <div className="card-body stack">
        <p style={{ color: 'var(--ink-3)', margin: 0, fontSize: 13 }}>{t.transfer.templateHint}</p>

        {error ? (
          <div className="notice danger" role="alert">
            {error}
          </div>
        ) : null}
        {done ? (
          <div className="notice ok">
            <span>{done}</span>
          </div>
        ) : null}

        <Field label={t.transfer.chooseFile}>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.csv"
            onChange={(e) => {
              const chosen = e.target.files?.[0];
              if (chosen) void choose(chosen);
            }}
          />
        </Field>

        {busy === 'checking' ? (
          <p style={{ color: 'var(--ink-3)', margin: 0 }}>
            <Spinner /> {t.transfer.checking}
          </p>
        ) : null}

        {report ? (
          <>
            <div className="notice">
              <span>
                {t.transfer.checked
                  .replace('{total}', String(report.total))
                  .replace('{ready}', String(report.ready))
                  .replace('{duplicates}', String(report.duplicates))
                  .replace('{errors}', String(report.errors))}
              </span>
            </div>

            {report.duplicates > 0 ? (
              <label className="row" style={{ gap: 10, alignItems: 'flex-start' }}>
                <input
                  type="checkbox"
                  checked={withDuplicates}
                  onChange={(e) => setWithDuplicates(e.target.checked)}
                  style={{ marginTop: 3 }}
                />
                <span>
                  <strong>{t.transfer.includeDuplicates}</strong>
                  <span style={{ display: 'block', color: 'var(--ink-3)', fontSize: 12 }}>
                    {t.transfer.includeDuplicatesHint}
                  </span>
                </span>
              </label>
            ) : null}

            <div className="table-scroll" style={{ maxHeight: 360, overflowY: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th>{t.transfer.line}</th>
                    <th>{t.transfer.rowName}</th>
                    <th>{t.transfer.rowStatus}</th>
                    <th>{t.transfer.rowProblem}</th>
                  </tr>
                </thead>
                <tbody>
                  {report.rows.map((row) => (
                    <tr key={row.line}>
                      <td className="ltr-value">{row.line}</td>
                      <td>{row.name ?? '—'}</td>
                      <td>
                        <span
                          className={
                            row.status === 'ready'
                              ? 'badge ok'
                              : row.status === 'duplicate'
                                ? 'badge warn'
                                : 'badge danger'
                          }
                        >
                          {row.status === 'ready'
                            ? t.transfer.statusReady
                            : row.status === 'duplicate'
                              ? t.transfer.statusDuplicate
                              : t.transfer.statusError}
                        </span>
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                        {row.existing
                          ? t.transfer.looksLike
                              .replace('{name}', row.existing.name)
                              .replace('{number}', row.existing.patientNumber)
                          : (row.message ?? '—')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="row" style={{ justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn"
                disabled={busy !== null || importable === 0}
                onClick={() => void commit()}
              >
                {busy === 'importing' ? (
                  <>
                    <Spinner /> {t.transfer.importing}
                  </>
                ) : importable === 0 ? (
                  t.transfer.nothingToImport
                ) : (
                  t.transfer.importReady.replace('{n}', String(importable))
                )}
              </button>
            </div>
          </>
        ) : null}

        {!report && busy === null && !file ? (
          <EmptyState title={t.transfer.importHint} />
        ) : null}
      </div>
    </div>
  );
}
